import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Check,
  Copy,
  KeyRound,
  Link2,
  MessageCircle,
  Plus,
  ShieldAlert,
  Trash2,
  Wifi,
  WifiOff,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  type StatusTone,
} from '@aeris/ui'

import { ChannelAccountForm } from '../components/channel-account-form.tsx'
import { ChannelDiagnostics } from '../components/channel-diagnostics.tsx'
import { ChannelOwnerPicker } from '../components/channel-owner-picker.tsx'
import { ChannelPairingDialog } from '../components/channel-pairing-dialog.tsx'
import { TabHeader } from '../components/config-tabs.tsx'
import {
  ApiError,
  checkChannelConnection,
  deleteChannelAccount,
  fetchChannelAccounts,
  logoutChannelDevice,
  type ChannelAccountRemovalType,
  type ChannelAccountSummaryType,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { formatWaId } from '../lib/inbox.ts'

/**
 * Atendimento → Canais: a conta de WhatsApp que fala com o gateway (F045).
 *
 * Duas coisas que esta tela existe para dizer, sem as quais ninguém termina
 * de configurar:
 *
 *   1. **A URL do webhook**, pronta para copiar — é o endereço que alguém tem
 *      que colar no painel do OpenBSP. Sem ele cadastrado lá, a conta existe
 *      aqui e nenhuma mensagem chega nunca.
 *   2. **Quando falta `CHANNEL_SECRET_KEY`**, toda escrita nesta conta é
 *      recusada com 409 nomeando a variável — `channel-account-form.tsx`
 *      mostra essa frase como orientação, não como erro genérico, o mesmo
 *      padrão da F032 para uma chave de ambiente que ainda não existe.
 *
 * Uma conta **desconectada** ganha destaque na lista — não só a cor do selo —
 * porque a coexistência do WhatsApp derruba o vínculo por conta própria (o
 * dono reconecta pelo próprio celular) e alguém do lado do Aeris precisa
 * perceber isso, não só saber que existe um estado chamado "disconnected".
 *
 * Nenhuma conversa aparece aqui: isso é a F046. Esta tela só cadastra o canal.
 *
 * ## Aparelho vinculado (F192)
 *
 * A conta pela WuzAPI nasce na Plataforma, não aqui: o vínculo mexe no
 * servidor de WhatsApp de todos os clientes, e a loja não o enxerga. O que a
 * loja faz com ela é o que o celular faz — **conectar** (QR ou código),
 * **verificar** e **desconectar**. Sem organização, sem ID de número, sem
 * webhook para colar, sem diagnóstico do OpenBSP, sem apagar.
 */

const STATUS_LABELS: Record<string, string> = {
  active: 'Ativa',
  disconnected: 'Desconectada',
  pairing: 'Aguardando conexão',
}

const STATUS_TONES: Record<string, StatusTone> = {
  active: 'success',
  disconnected: 'danger',
  pairing: 'warning',
}

/**
 * O que a exclusão destruiu, em português e sem zeros (F076).
 *
 * Listar "0 conversas, 0 mensagens, 0 chamados…" seria ruído no caso mais
 * comum — a conta cadastrada errada, que nunca recebeu nada. Só o que tem
 * número aparece, e quando não há nada a lista vira "nenhum dado".
 *
 * `detachedEvents` fica fora: eles NÃO foram destruídos, foram soltos da
 * conta. Contá-los aqui seria dizer que sumiram.
 */
const describeRemoval = (removal: ChannelAccountRemovalType): string => {
  const partes: string[] = []
  const diz = (total: number, um: string, muitos: string): void => {
    if (total > 0) partes.push(`${String(total)} ${total === 1 ? um : muitos}`)
  }
  diz(removal.conversations, 'conversa', 'conversas')
  diz(removal.messages, 'mensagem', 'mensagens')
  diz(removal.contacts, 'contato', 'contatos')
  diz(removal.tickets, 'chamado', 'chamados')
  diz(removal.templates, 'modelo', 'modelos')
  diz(removal.rules, 'regra de follow-up', 'regras de follow-up')
  diz(removal.campaigns, 'campanha', 'campanhas')

  return partes.length === 0 ? 'nenhum dado' : partes.join(', ')
}

/** A URL pronta para colar no OpenBSP. Estática — não depende de nenhuma conta. */
const WebhookUrlCallout = (): ReactNode => {
  const [copied, setCopied] = useState(false)
  const webhookUrl = `${window.location.origin}/api/v1/communication/openbsp/webhook`

  return (
    <section className="flex flex-col gap-2 rounded-(--radius-lg) border border-(--border-default) bg-(--surface) p-4">
      <h2 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
        URL do webhook
      </h2>
      <p className="text-(length:--text-xs) text-(--text-secondary)">
        Registre este endereço no painel do OpenBSP para que as mensagens
        cheguem ao Aeris. Sem isso cadastrado lá, nenhuma conta abaixo recebe
        nada.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-(--radius-md) border border-(--border-default) bg-(--surface-sunken) px-3 py-2 font-(--font-mono) text-(length:--text-sm) text-(--text-primary) select-all">
          {webhookUrl}
        </code>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leadingIcon={
            copied ? (
              <Check aria-hidden="true" className="h-3.5 w-3.5" />
            ) : (
              <Copy aria-hidden="true" className="h-3.5 w-3.5" />
            )
          }
          onClick={() => {
            /*
             * Best effort, como em `member-form.tsx`: a Clipboard API pode
             * faltar fora de um contexto seguro — uma loja servida em HTTP
             * puro na LAN é um caso real — e a URL continua legível e
             * selecionável na tela mesmo se o botão falhar.
             */
            try {
              void navigator.clipboard.writeText(webhookUrl).then(
                () => {
                  setCopied(true)
                },
                () => {
                  setCopied(false)
                }
              )
            } catch {
              setCopied(false)
            }
          }}
        >
          {copied ? 'Copiada' : 'Copiar'}
        </Button>
      </div>
    </section>
  )
}

/**
 * A linha de uma conta de aparelho vinculado (F192).
 *
 * "Aguardando conexão" também ganha destaque: é a conta que a Plataforma
 * acabou de vincular e que ninguém leu o QR ainda — nada chega por ela até
 * alguém da loja pegar o celular.
 */
const LinkedDeviceRow = ({
  account,
  canWrite,
  busy,
  onConnect,
  onCheck,
  onLogout,
}: {
  account: ChannelAccountSummaryType
  canWrite: boolean
  busy: boolean
  onConnect: () => void
  onCheck: () => void
  onLogout: () => void
}): ReactNode => (
  <TableRow
    className={
      account.status === 'disconnected'
        ? 'bg-(--danger-subtle)'
        : account.status === 'pairing'
          ? 'bg-(--warning-subtle)'
          : undefined
    }
  >
    <TableCell>{account.displayName}</TableCell>
    <TableCell>
      <Badge
        tone="neutral"
        icon={<Link2 aria-hidden="true" className="h-3 w-3" />}
      >
        Aparelho vinculado
      </Badge>
    </TableCell>
    <TableCell className="font-(--font-mono)">
      {account.linkedPhone === null ? (
        <span className="font-(--font-sans) text-(--text-muted)">
          Nenhum celular conectado
        </span>
      ) : (
        formatWaId(account.linkedPhone)
      )}
    </TableCell>
    <TableCell>
      <Badge
        tone={account.hasCredentials ? 'success' : 'warning'}
        icon={<KeyRound aria-hidden="true" className="h-3 w-3" />}
      >
        {account.hasCredentials ? 'Pela plataforma' : 'Pendentes'}
      </Badge>
    </TableCell>
    <TableCell>
      <ChannelOwnerPicker account={account} />
    </TableCell>
    <TableCell>
      <div className="flex flex-col gap-1">
        <Badge
          tone={STATUS_TONES[account.status] ?? 'neutral'}
          icon={
            account.status === 'active' ? (
              <Wifi aria-hidden="true" className="h-3 w-3" />
            ) : (
              <WifiOff aria-hidden="true" className="h-3 w-3" />
            )
          }
        >
          {STATUS_LABELS[account.status] ?? account.status}
        </Badge>
        {account.status !== 'active' && account.lastError !== null ? (
          <span className="flex items-center gap-1 text-(length:--text-xs) text-(--danger)">
            <ShieldAlert aria-hidden="true" className="h-3 w-3 shrink-0" />
            {account.lastError}
          </span>
        ) : account.status === 'pairing' ? (
          <span className="text-(length:--text-xs) text-(--text-secondary)">
            Leia o QR com o celular da loja
          </span>
        ) : null}
      </div>
    </TableCell>
    <TableCell>
      <div className="flex justify-end gap-1">
        {canWrite && account.status !== 'active' ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={onConnect}
            aria-label={`Conectar ${account.displayName}`}
          >
            Conectar
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          loading={busy}
          onClick={onCheck}
          aria-label={`Verificar ${account.displayName}`}
        >
          Verificar
        </Button>
        {canWrite && account.status === 'active' ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onLogout}
            aria-label={`Desconectar ${account.displayName}`}
          >
            Desconectar
          </Button>
        ) : null}
      </div>
    </TableCell>
  </TableRow>
)

export const AtendimentoCanaisPage = (): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()
  const canWrite = allows('communication.channel.write')

  const [formOpen, setFormOpen] = useState(false)
  /** A conta cujo diagnóstico está aberto — só um por vez. */
  const [diagnosing, setDiagnosing] = useState<string | null>(null)
  const [editing, setEditing] = useState<ChannelAccountSummaryType | undefined>(
    undefined
  )
  /** A conta que a pessoa pediu para apagar — o diálogo lê o nome dela. */
  const [removing, setRemoving] = useState<ChannelAccountSummaryType | null>(
    null
  )
  /**
   * A recusa do servidor, mostrada NA TELA e não no diálogo, porque o diálogo
   * fecha ao confirmar.
   */
  const [removeError, setRemoveError] = useState<string | null>(null)
  /**
   * O que a exclusão destruiu (F076).
   *
   * Desde que apagar deixou de recusar quando há histórico, esta linha é a
   * única defesa que sobrou: a pessoa confirma e VÊ o tamanho do que sumiu,
   * em vez de a destruição acontecer em silêncio.
   */
  const [removed, setRemoved] = useState<ChannelAccountRemovalType | null>(null)
  /** A conta de aparelho vinculado sendo conectada (QR ou código). */
  const [pairing, setPairing] = useState<ChannelAccountSummaryType | null>(null)
  const [loggingOut, setLoggingOut] =
    useState<ChannelAccountSummaryType | null>(null)
  /** O resultado de "Verificar" / "Desconectar", na tela e não num toast. */
  const [deviceNotice, setDeviceNotice] = useState<{
    tone: 'success' | 'warning' | 'danger'
    text: string
  } | null>(null)

  const refreshAccounts = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['communication-channel-accounts'],
    })
  }

  const deviceFailure = (cause: unknown): void => {
    setDeviceNotice({
      tone: 'danger',
      text:
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível falar com o servidor de WhatsApp agora.',
    })
  }

  const check = useMutation({
    mutationFn: async (account: ChannelAccountSummaryType) =>
      await checkChannelConnection(account.id),
    onSuccess: async (result) => {
      setDeviceNotice(
        result.loggedIn && result.connected
          ? {
              tone: 'success',
              text: `${result.account.displayName} está conectado${
                result.phone === null ? '' : ` (${formatWaId(result.phone)})`
              }.`,
            }
          : result.loggedIn
            ? {
                tone: 'warning',
                text: `${result.account.displayName} está pareado, mas o servidor perdeu a conexão com o WhatsApp. Ele tenta de novo sozinho; se não voltar em alguns minutos, verifique o celular.`,
              }
            : {
                tone: 'warning',
                text: `${result.account.displayName} não está conectado. Use "Conectar" e leia o QR com o celular da loja.`,
              }
      )
      await refreshAccounts()
    },
    onError: deviceFailure,
  })

  const logout = useMutation({
    mutationFn: async (account: ChannelAccountSummaryType) =>
      await logoutChannelDevice(account.id),
    onSuccess: async (account) => {
      setDeviceNotice({
        tone: 'success',
        text: `${account.displayName} foi desconectado. O histórico continua aqui.`,
      })
      await refreshAccounts()
    },
    onError: deviceFailure,
  })

  const remove = useMutation({
    mutationFn: async (id: string) => await deleteChannelAccount(id),
    onSuccess: async (result) => {
      setRemoveError(null)
      setRemoved(result)
      await queryClient.invalidateQueries({
        queryKey: ['communication-channel-accounts'],
      })
    },
    onError: (error: unknown) => {
      setRemoved(null)
      setRemoveError(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível apagar esta conta agora.'
      )
    },
  })

  const accounts = useQuery({
    queryKey: ['communication-channel-accounts'],
    queryFn: async ({ signal }) => await fetchChannelAccounts(signal),
  })

  const rows = accounts.data ?? []
  /*
   * O endereço do webhook do OpenBSP só interessa a quem tem (ou vai
   * cadastrar) conta da API oficial. Numa loja só com aparelho vinculado ele
   * seria uma instrução para colar um endereço num painel que ela não usa.
   */
  const showOpenbspWebhook =
    rows.length === 0 || rows.some((row) => row.connection === 'cloud_api')

  return (
    <>
      <TabHeader
        title="Canais de atendimento"
        description="Os números de WhatsApp que atendem nesta loja."
        actions={
          canWrite ? (
            <Button
              onClick={() => {
                setEditing(undefined)
                setFormOpen(true)
              }}
            >
              <Plus aria-hidden="true" /> Nova conta
            </Button>
          ) : null
        }
      />

      {showOpenbspWebhook ? <WebhookUrlCallout /> : null}

      {deviceNotice === null ? null : (
        <InlineAlert tone={deviceNotice.tone}>{deviceNotice.text}</InlineAlert>
      )}

      {accounts.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : accounts.isError ? (
        <EmptyState
          icon={<MessageCircle aria-hidden="true" className="h-8 w-8" />}
          title="Não foi possível carregar os canais"
          description="Verifique a conexão com o servidor e tente novamente."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<MessageCircle aria-hidden="true" className="h-8 w-8" />}
          title="Nenhuma conta cadastrada"
          description={
            canWrite
              ? 'Cadastre a conta de WhatsApp da loja, com a organização e o número do OpenBSP, para começar a atender por aqui.'
              : 'Peça a um administrador para cadastrar a conta de WhatsApp da loja.'
          }
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Conta</TableHeaderCell>
                <TableHeaderCell>Conexão</TableHeaderCell>
                <TableHeaderCell>Número</TableHeaderCell>
                <TableHeaderCell>Credenciais</TableHeaderCell>
                <TableHeaderCell>Quem atende</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((account) =>
                account.connection === 'linked_device' ? (
                  <LinkedDeviceRow
                    key={account.id}
                    account={account}
                    canWrite={canWrite}
                    busy={
                      (check.isPending && check.variables.id === account.id) ||
                      (logout.isPending && logout.variables.id === account.id)
                    }
                    onConnect={() => {
                      setDeviceNotice(null)
                      setPairing(account)
                    }}
                    onCheck={() => {
                      setDeviceNotice(null)
                      check.mutate(account)
                    }}
                    onLogout={() => {
                      setDeviceNotice(null)
                      setLoggingOut(account)
                    }}
                  />
                ) : (
                  <TableRow
                    key={account.id}
                    // Destaque de verdade, não só o selo: a coexistência derruba
                    // o vínculo por conta própria, e uma linha que se parece com
                    // as outras é uma linha que ninguém nota até faltar mensagem.
                    className={
                      account.status === 'disconnected'
                        ? 'bg-(--danger-subtle)'
                        : undefined
                    }
                  >
                    <TableCell>{account.displayName}</TableCell>
                    <TableCell className="font-(--font-mono)">
                      {account.openbspOrgId}
                    </TableCell>
                    <TableCell className="font-(--font-mono)">
                      {account.address}
                    </TableCell>
                    <TableCell>
                      <Badge
                        tone={account.hasCredentials ? 'success' : 'warning'}
                        icon={
                          <KeyRound aria-hidden="true" className="h-3 w-3" />
                        }
                      >
                        {account.hasCredentials ? 'Configuradas' : 'Pendentes'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <ChannelOwnerPicker account={account} />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge
                          tone={STATUS_TONES[account.status] ?? 'neutral'}
                          icon={
                            account.status === 'active' ? (
                              <Wifi aria-hidden="true" className="h-3 w-3" />
                            ) : (
                              <WifiOff aria-hidden="true" className="h-3 w-3" />
                            )
                          }
                        >
                          {STATUS_LABELS[account.status] ?? account.status}
                        </Badge>
                        {account.status === 'disconnected' ? (
                          <span className="flex items-center gap-1 text-(length:--text-xs) text-(--danger)">
                            <ShieldAlert
                              aria-hidden="true"
                              className="h-3 w-3 shrink-0"
                            />
                            Reconecte no WhatsApp Business
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-expanded={diagnosing === account.id}
                          onClick={() => {
                            setDiagnosing(
                              diagnosing === account.id ? null : account.id
                            )
                          }}
                        >
                          {diagnosing === account.id
                            ? 'Fechar diagnóstico'
                            : 'Diagnóstico'}
                        </Button>
                        {canWrite ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditing(account)
                              setFormOpen(true)
                            }}
                            aria-label={`Editar ${account.displayName}`}
                          >
                            Editar
                          </Button>
                        ) : null}
                        {canWrite ? (
                          /*
                           * Escrito, não só a lixeira. Em lista onde apagar é a
                           * única ação (membro de fila, endereço de cadastro) o
                           * ícone sozinho basta e é a convenção do app. Aqui ele
                           * fica em terceiro, depois de dois botões com palavra:
                           * entre "Diagnóstico" e "Editar", um desenho cinza lê
                           * como enfeite, e o dono procurou o botão de excluir
                           * nesta tela sem achar.
                           */
                          <Button
                            variant="ghost"
                            size="sm"
                            leadingIcon={
                              <Trash2 aria-hidden="true" className="h-4 w-4" />
                            }
                            onClick={() => {
                              setRemoveError(null)
                              setRemoved(null)
                              setRemoving(account)
                            }}
                            aria-label={`Apagar ${account.displayName}`}
                          >
                            Apagar
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/*
        F063 — o diagnóstico FORA da tabela, não numa linha expandida: ele
        tem oito itens com duas linhas de texto cada, e uma célula que cresce
        assim empurra as colunas vizinhas de um jeito que ninguém consegue
        ler. Fica abaixo, com o nome da conta no título.
      */}
      {diagnosing === null ? null : (
        <ChannelDiagnostics accountId={diagnosing} />
      )}

      {removeError === null ? null : (
        <p
          role="alert"
          className="mt-3 text-(length:--text-sm) text-(--danger)"
        >
          {removeError}
        </p>
      )}

      {removed === null ? null : (
        <p
          role="status"
          className="mt-3 text-(length:--text-sm) text-(--text-secondary)"
        >
          Conta apagada. Foram destruídos {describeRemoval(removed)}.
        </p>
      )}

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null)
        }}
        title={removing === null ? '' : `Apagar ${removing.displayName}?`}
        /*
         * A frase completa, nunca "Tem certeza?" (docs/UI_UX.md) — e ela diz
         * as duas coisas que decidem: o identificador sai do índice (então
         * evento nenhum encontra mais esta loja), e uma conta com histórico
         * não é apagada de jeito nenhum, o que evita a pessoa achar que vai
         * perder conversa.
         */
        /*
         * A frase completa, nunca "Tem certeza?" (docs/UI_UX.md). Desde a
         * F076 ela precisa dizer a parte cara: as conversas dos clientes vão
         * junto. Era isso que o banco impedia até ontem.
         */
        description={
          removing === null
            ? ''
            : `Apaga junto as conversas, mensagens, chamados, contatos, modelos, regras de follow-up e campanhas desta conta — não tem volta. O identificador ${removing.address} deixa de apontar para esta loja, e nenhum evento do gateway chega mais por ele.`
        }
        confirmLabel="Apagar conta e os dados"
        tone="danger"
        loading={remove.isPending}
        onConfirm={() => {
          const target = removing
          setRemoving(null)
          if (target === null) return
          remove.mutate(target.id)
        }}
      />

      <ConfirmDialog
        open={loggingOut !== null}
        onOpenChange={(open) => {
          if (!open) setLoggingOut(null)
        }}
        title={
          loggingOut === null ? '' : `Desconectar ${loggingOut.displayName}?`
        }
        description="O Aeris sai de “Aparelhos conectados” no celular da loja e para de receber e enviar por este número. As conversas continuam aqui, e para voltar basta conectar de novo."
        confirmLabel="Desconectar"
        tone="danger"
        loading={logout.isPending}
        onConfirm={() => {
          const target = loggingOut
          setLoggingOut(null)
          if (target !== null) logout.mutate(target)
        }}
      />

      {pairing === null ? null : (
        <ChannelPairingDialog
          account={pairing}
          onClose={() => {
            setPairing(null)
          }}
        />
      )}

      {/* Chaveado e condicional: fechar o diálogo descarta o formulário. */}
      {formOpen ? (
        <ChannelAccountForm
          key={editing?.id ?? 'new'}
          open
          account={editing}
          onOpenChange={setFormOpen}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: ['communication-channel-accounts'],
            })
          }}
        />
      ) : null}
    </>
  )
}
